import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import Box from '@mui/material/Box';
import { DEFAULT_VIDEO_CROSSORIGIN } from 'shared/constants/video-constants';

/**
 * This interface describes a format for video sources in videojs, it tries to mirror
 * the standard interface of video element
 */
export interface SourceVideoJS {
  src: string;
  type?: string;
  // this is from videojs-hls-streaming, it controls the credentials for the segments
  // see https://github.com/videojs/http-streaming?tab=readme-ov-file#options
  withCredentials?: boolean;
}

/**
 * Interface describing the parameteres for the VideoJS player
 */
interface VideoJSPlayerProps {
  source: SourceVideoJS;
}

/**
 * This component is a videoJS powered videoplayer, which gets the source data (it is now in single source mode)
 * It controls the player through two refs, and controls the player disposal through the effect.
 * @param props, contains sources for now
 * @returns
 */
const VideoJSPlayer = ({ source }: VideoJSPlayerProps) => {
  // const hlsStreamSource: SourceVideoJS = {
  //   src: hlsStreamURL,
  //   type: 'application/x-mpegURL',
  //   withCredentials: false,
  // };

  // this ref is the main one, and it is employed in the rendering
  const videoRef = useRef<HTMLDivElement>(null);

  // this is a ref, which is used solely to have our player persistent through renders
  const playerRef = useRef<Player>(null);

  function setupVideoJSPlayer() {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');

      // this class is optional, but it is a nice default for the styling
      videoElement.classList.add('vjs-big-play-centered');

      // Set crossorigin on the element before Video.js initializes or sets sources
      videoElement.setAttribute('crossorigin', DEFAULT_VIDEO_CROSSORIGIN);

      // adding the empty element to the ref.
      videoRef.current.appendChild(videoElement);
      // forming the options for player, read more about them in https://videojs.com/guides/options/
      const videoJSOptions = {
        autoplay: false, // autoplay is often shadowbanned
        controls: true,
        responsive: true,
        fluid: true,
        sources: [source.src],
      };

      // it returns the html element actually, but encapsulated in some way
      const videoJSPlayer = videojs(videoElement, videoJSOptions);

      // spawning the player
      playerRef.current = videoJSPlayer;
    }

    /**
     * This function is a function destroyer for videojs player.
     * It basically checks if it is ran, and if it is not, it disposes it properly.
     */
    function destroyVideoJSPlayer() {
      const currentPlayer = playerRef.current;

      // if the player is initialized and not disposed, it disposes it properly
      if (currentPlayer && !currentPlayer.isDisposed()) {
        currentPlayer.dispose();
        playerRef.current = null;
      }
    }
    return destroyVideoJSPlayer;
  }
  useEffect(setupVideoJSPlayer, [playerRef, source.src]);

  return (
    <Box
      data-vjs-player
      sx={{
        width: '100%',
        maxHeight: '610px',
        minHeight: '178px',
      }}
    >
      {/** The html datatype is needed for vjs internal purposes */}
      <Box
        ref={videoRef}
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '610px',
          minHeight: '178px',
          '& .video-js': {
            borderRadius: '8px',
            overflow: 'hidden',
            width: '100%',
            height: 'auto',
            maxHeight: '610px',
            minHeight: '178px',
            aspectRatio: '16/9',
            '&.vjs-fluid': {
              width: '100%',
              height: 'auto',
              maxHeight: '610px',
              minHeight: '178px',
              paddingTop: '0',
            },
          },
          '& .video-js .vjs-tech': {
            borderRadius: '8px',
            width: '100%',
            height: '100%',
            maxHeight: '610px',
            objectFit: 'contain',
          },
          '& .video-js video': {
            borderRadius: '8px',
          },
        }}
      />
    </Box>
  );
};
export { VideoJSPlayerProps };
export default VideoJSPlayer;
